import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  Activity,
  ArrowLeftRight,
  ArrowUpDown,
  Camera,
  Check,
  Eye,
  Gamepad2,
  Mic2,
  Move,
  Play,
  RotateCcw,
  Save,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { createCalibrationProfile, DEFAULT_CONTROL_SETTINGS } from "./controlDefaults";
import { arcadeAudio } from "./audio";
import { publicUrl } from "./publicUrl";
import { balloonPosition, balloonScaleAt, CAMPAIGN_LEVEL_COUNT, CAMPAIGN_TOTAL_BALLOONS, createLevelOne, portalScreenPosition } from "./game/model";
import type { CalibrationProfile, GameResult, Pose, TrackingStatus, TriggerMode } from "./types";

export function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand-mark ${compact ? "brand-mark--compact" : ""}`} aria-label="Bombs Away!">
      <span className="brand-kicker">Backyard defense system</span>
      <div className="brand-title">
        <span>Bombs</span>
        <strong>Away!</strong>
      </div>
      {!compact && <p>Water balloons incoming. Face forward. Fire when ready.</p>}
    </div>
  );
}

type MenuProps = {
  onPlay: () => void;
  onDemo: () => void;
};

export function MainMenu({ onPlay, onDemo }: MenuProps) {
  return (
    <main className="menu-screen screen-enter">
        <img className="menu-key-art" src={publicUrl("assets/backyard-lab/welcome-key-art-v4.png")} alt="" />
      <div className="menu-cinematic-shade" aria-hidden="true" />
      <section className="menu-copy">
        <img className="menu-logo" src={publicUrl("assets/backyard-lab/logo-v1.png")} alt="Bombs Away!" />
        <div className="status-pill">
          <span className="status-dot status-dot--ready" />
          Camera mapping automatic
        </div>
        <p className="menu-tagline">Your backyard. Their balloons.<br /><strong>One wildly over-engineered defense system.</strong></p>
        <div className="menu-campaign-summary" aria-label={`${CAMPAIGN_LEVEL_COUNT} level campaign, ${CAMPAIGN_TOTAL_BALLOONS} balloons, four hit shield`}>
          <span><strong>{CAMPAIGN_LEVEL_COUNT}</strong> levels</span>
          <i />
          <span><strong>{CAMPAIGN_TOTAL_BALLOONS}</strong> balloons</span>
          <i />
          <span><strong>4</strong> hit shield</span>
        </div>
        <div className="menu-actions menu-actions--simple">
          <button className="button button--primary button--huge" onClick={onPlay}>
            <Play fill="currentColor" />
            Start campaign
          </button>
        </div>
        <button className="text-button" onClick={onDemo}>
          <Gamepad2 /> Preview with pointer controls
        </button>
      </section>
      <aside className="menu-field-note" aria-label="Game objective">
        <span>Field note 01</span>
        <strong>10 levels.<br />5 to 50 balloons.</strong>
      </aside>
      <footer className="menu-footer">
        <span>Move your head to line up the world</span>
        <span className="footer-separator" />
        <span>Open your mouth, blink, or say “Pew!” to fire</span>
      </footer>
    </main>
  );
}

type OnboardingProps = {
  onContinue: () => void;
  onBack: () => void;
};

export function OnboardingScreen({ onContinue, onBack }: OnboardingProps) {
  return (
    <main className="onboarding-screen screen-enter" aria-label="One player camera instructions">
      <div className="onboarding-stage">
        <img
          className="onboarding-art"
          src={publicUrl("assets/backyard-lab/onboarding-one-player-v1.png")}
          alt="One player at a time. Stand in the camera zone, move only your head while keeping your body still, and have the other players wait for their turn."
        />
        <button className="onboarding-continue" onClick={onContinue} aria-label="Got it, start the camera">
          <span className="sr-only">Got it, start the camera</span>
        </button>
      </div>
      <button className="onboarding-back icon-button" onClick={onBack} aria-label="Back to main menu">
        <X />
      </button>
    </main>
  );
}

type MovementWarmupProps = {
  status: TrackingStatus;
  error: string | null;
  pose: Pose;
  profile: CalibrationProfile;
  reducedMotion: boolean;
  setVideoElement: (node: HTMLVideoElement | null) => void;
  startCamera: () => Promise<void>;
  onComplete: () => void;
  onBack: () => void;
};

export function MovementWarmup({ status, error, pose, profile, reducedMotion, setVideoElement, startCamera, onComplete, onBack }: MovementWarmupProps) {
  const [phase, setPhase] = useState<"horizontal" | "vertical" | "ready">("horizontal");
  const [reached, setReached] = useState({ left: false, right: false, up: false, down: false });
  const phaseChangeTimer = useRef<number | null>(null);
  const fieldRef = useRef<HTMLElement | null>(null);
  const targetPoseRef = useRef<Pose>(pose);
  const renderPoseRef = useRef<Pose>({ x: 0, y: 0, z: 0, confidence: 0 });

  useEffect(() => () => {
    if (phaseChangeTimer.current) window.clearTimeout(phaseChangeTimer.current);
  }, []);

  useEffect(() => {
    targetPoseRef.current = pose;
  }, [pose]);

  useEffect(() => {
    let frame = 0;
    let previous = performance.now();
    const animate = (now: number) => {
      const delta = Math.min(50, now - previous);
      previous = now;
      const responseMs = reducedMotion ? 1 : 24 + profile.smoothing * 180;
      const alpha = 1 - Math.exp(-delta / responseMs);
      const current = renderPoseRef.current;
      const target = targetPoseRef.current;
      current.x += (target.x - current.x) * alpha;
      current.y += (target.y - current.y) * alpha;
      current.z = (current.z ?? 0) + ((target.z ?? 0) - (current.z ?? 0)) * alpha;
      current.confidence = target.confidence;
      const imageShiftX = -profile.viewport.framing.x * 50 - current.x * profile.viewport.pan.x * 100 / profile.viewport.zoom;
      const imageShiftY = profile.viewport.framing.y * 50 + current.y * profile.viewport.pan.y * 100 / profile.viewport.zoom;
      fieldRef.current?.style.setProperty("--view-x", `${imageShiftX}%`);
      fieldRef.current?.style.setProperty("--view-y", `${imageShiftY}%`);
      frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, [profile.smoothing, profile.viewport, reducedMotion]);

  useEffect(() => {
    if (status !== "tracking" || phase === "ready") return;
    setReached((current) => {
      const next = {
        left: current.left || (phase === "horizontal" && pose.x <= -0.28),
        right: current.right || (phase === "horizontal" && pose.x >= 0.28),
        up: current.up || (phase === "vertical" && pose.y >= 0.28),
        down: current.down || (phase === "vertical" && pose.y <= -0.28),
      };
      return next.left === current.left && next.right === current.right && next.up === current.up && next.down === current.down ? current : next;
    });
  }, [phase, pose.x, pose.y, status]);

  useEffect(() => {
    if (phase === "horizontal" && reached.left && reached.right) {
      arcadeAudio.play("capture");
      phaseChangeTimer.current = window.setTimeout(() => setPhase("vertical"), 520);
    }
    if (phase === "vertical" && reached.up && reached.down) {
      arcadeAudio.play("ready");
      phaseChangeTimer.current = window.setTimeout(() => setPhase("ready"), 520);
    }
    return () => {
      if (phaseChangeTimer.current) window.clearTimeout(phaseChangeTimer.current);
    };
  }, [phase, reached.down, reached.left, reached.right, reached.up]);

  const tracking = status === "tracking";
  const loading = status === "requesting" || status === "loading-model";

  return (
    <main className="warmup-screen screen-enter">
      <header className="warmup-topbar">
        <button className="icon-button" onClick={onBack} aria-label="Back to main menu"><X /></button>
        <div><span>Movement warm-up</span><strong>{phase === "horizontal" ? "1 / 2" : phase === "vertical" ? "2 / 2" : "Ready"}</strong></div>
        <button className="warmup-skip" onClick={onComplete}>Skip warm-up</button>
      </header>
      <section
        ref={fieldRef}
        className={`warmup-camera warmup-camera--${phase}`}
        style={{
          "--view-zoom": `${profile.viewport.zoom * 100}%`,
        } as CSSProperties}
      >
        <img className="warmup-field" src={publicUrl("assets/backyard-lab/gameplay-field-v1.png")} alt="The backyard game view" />
        <div className="warmup-shade" />
        <div className="warmup-video-inset"><video ref={setVideoElement} muted playsInline /><small>Camera position</small></div>
        {phase !== "ready" && (
          <>
            <div className={`warmup-goal warmup-goal--left ${reached.left ? "is-complete" : ""}`}><Check /></div>
            <div className={`warmup-goal warmup-goal--right ${reached.right ? "is-complete" : ""}`}><Check /></div>
            <div className={`warmup-goal warmup-goal--up ${reached.up ? "is-complete" : ""}`}><Check /></div>
            <div className={`warmup-goal warmup-goal--down ${reached.down ? "is-complete" : ""}`}><Check /></div>
            <div className="warmup-reticle" aria-hidden="true"><span /></div>
          </>
        )}
        <div className="warmup-copy">
          <span className={`warmup-lock ${tracking ? "is-locked" : ""}`}><i />{tracking ? "Face locked" : "Step into the camera zone"}</span>
          {phase === "horizontal" && <><h1>Look left. Look right.</h1><p>Keep your shoulders still. Feel the backyard slide naturally behind the fixed laser sight.</p></>}
          {phase === "vertical" && <><h1>Now look up. Look down.</h1><p>Use a comfortable range and feel how your camera position reveals the yard.</p></>}
          {phase === "ready" && <><h1>Movement mapped.</h1><p>You are ready to defend the backyard.</p><button className="button button--primary button--huge" onClick={onComplete}><Play fill="currentColor" />Start level 1</button></>}
        </div>
        {loading && <div className="warmup-loading"><span className="loader" />Starting camera tracking…</div>}
        {(status === "denied" || status === "error") && <div className="warmup-error"><Camera /><strong>Camera unavailable</strong><p>{error}</p><button className="button button--primary" onClick={() => void startCamera()}>Try again</button></div>}
      </section>
    </main>
  );
}

const CAPTURE_STEPS = [
  { label: "Set your center", hint: "Look at the glowing target in the middle of the screen", icon: Eye, target: { x: 50, y: 50 } },
  { label: "Reach top left", hint: "Move your head comfortably to place the target in this corner", icon: Move, target: { x: 8, y: 10 } },
  { label: "Reach top right", hint: "Use your maximum comfortable range—no shoulder movement needed", icon: Move, target: { x: 92, y: 10 } },
  { label: "Reach bottom right", hint: "Follow the target with your whole face, not only your eyes", icon: Move, target: { x: 92, y: 88 } },
  { label: "Reach bottom left", hint: "This final corner completes the screen-space map", icon: Move, target: { x: 8, y: 88 } },
];

type CalibrationProps = {
  status: TrackingStatus;
  error: string | null;
  rawPose: Pose;
  blend: { mouth: number; blink: number };
  fireSignal: number;
  voiceSupported: boolean;
  setVideoElement: (node: HTMLVideoElement | null) => void;
  startCamera: () => Promise<void>;
  selectedTrigger: TriggerMode;
  currentProfile: CalibrationProfile | null;
  onTriggerChange: (trigger: TriggerMode) => void;
  onComplete: (profile: CalibrationProfile) => void;
  onBack: () => void;
};

export function CalibrationScreen({
  status,
  error,
  rawPose,
  blend,
  fireSignal,
  voiceSupported,
  setVideoElement,
  startCamera,
  selectedTrigger,
  currentProfile,
  onTriggerChange,
  onComplete,
  onBack,
}: CalibrationProps) {
  const [step, setStep] = useState(0);
  const [capturing, setCapturing] = useState(false);
  const [samples, setSamples] = useState<Pose[][]>([[], [], [], [], []]);
  const [mode, setMode] = useState<"movement" | "trigger">("movement");
  const [testedFire, setTestedFire] = useState(false);
  const rawRef = useRef(rawPose);
  const initialFireRef = useRef(fireSignal);

  useEffect(() => {
    rawRef.current = rawPose;
  }, [rawPose]);

  useEffect(() => {
    void startCamera();
  }, [startCamera]);

  useEffect(() => {
    if (fireSignal > initialFireRef.current) setTestedFire(true);
  }, [fireSignal]);

  const capture = () => {
    if (status !== "tracking" || capturing) return;
    arcadeAudio.play("capture");
    setCapturing(true);
    const collected: Pose[] = [];
    const timer = window.setInterval(() => collected.push({ ...rawRef.current }), 55);
    window.setTimeout(() => {
      window.clearInterval(timer);
      setSamples((current) => current.map((entry, index) => (index === step ? collected : entry)));
      setCapturing(false);
      if (step === 4) setMode("trigger");
      else setStep((current) => current + 1);
    }, 950);
  };

  const mean = (entries: Pose[], key: "x" | "y", fallback: number) =>
    entries.length ? entries.reduce((sum, item) => sum + item[key], 0) / entries.length : fallback;

  const finish = () => {
    const neutral = {
      x: mean(samples[0], "x", 0.5),
      y: mean(samples[0], "y", 0.5),
      z: samples[0].length ? samples[0].reduce((sum, item) => sum + (item.z ?? 0.24), 0) / samples[0].length : 0.24,
    };
    const cornerMean = (indexes: number[], key: "x" | "y", fallback: number) => {
      const entries = indexes.flatMap((index) => samples[index]);
      return mean(entries, key, fallback);
    };
    const base = createCalibrationProfile({
      neutral,
      range: {
        left: Math.max(0.025, Math.abs(cornerMean([1, 4], "x", neutral.x - 0.1) - neutral.x)),
        right: Math.max(0.025, Math.abs(cornerMean([2, 3], "x", neutral.x + 0.1) - neutral.x)),
        up: Math.max(0.02, Math.abs(cornerMean([1, 2], "y", neutral.y - 0.07) - neutral.y)),
        down: Math.max(0.02, Math.abs(cornerMean([3, 4], "y", neutral.y + 0.07) - neutral.y)),
      },
      trigger: selectedTrigger,
    });
    arcadeAudio.play("ready");
    onComplete(
      currentProfile
        ? {
            ...base,
            sensitivity: currentProfile.sensitivity,
            smoothing: currentProfile.smoothing,
            flip: currentProfile.flip,
            viewport: currentProfile.viewport,
            thresholds: currentProfile.thresholds,
          }
        : base,
    );
  };

  const CurrentIcon = CAPTURE_STEPS[step].icon;
  const loading = status === "requesting" || status === "loading-model";
  const thresholds = currentProfile?.thresholds ?? DEFAULT_CONTROL_SETTINGS.thresholds;

  return (
    <main className="calibration-screen screen-enter">
      <header className="topbar">
        <button className="icon-button" onClick={onBack} aria-label="Back to main menu"><X /></button>
        <BrandMark compact />
        <span className="step-counter">{mode === "movement" ? `Step ${step + 1} of 5` : "Fire test"}</span>
      </header>

      <section className="calibration-layout">
        <div className="camera-panel">
          <video ref={setVideoElement} className="camera-feed" muted playsInline />
          <div className={`tracking-frame ${status === "tracking" ? "tracking-frame--locked" : ""}`}>
            <span className="corner corner-tl" /><span className="corner corner-tr" />
            <span className="corner corner-bl" /><span className="corner corner-br" />
          </div>
          <div className="camera-status">
            <span className={`status-dot ${status === "tracking" ? "status-dot--ready" : ""}`} />
            {status === "tracking" ? "Face locked" : loading ? "Starting camera…" : status === "lost" ? "Find your face" : "Camera offline"}
          </div>
          {loading && <div className="camera-loading"><span className="loader" />{status === "loading-model" ? "Loading face tracker…" : "Requesting camera…"}</div>}
          {error && <div className="camera-error"><Camera />{error}<button onClick={() => void startCamera()}>Try again</button></div>}
          <div
            className="face-puck"
            style={{ left: `${rawPose.x * 100}%`, top: `${rawPose.y * 100}%`, opacity: status === "tracking" ? 1 : 0 }}
          />
          {mode === "movement" && (
            <div
              className="calibration-target"
              style={{ left: `${CAPTURE_STEPS[step].target.x}%`, top: `${CAPTURE_STEPS[step].target.y}%` }}
              aria-hidden="true"
            ><span /></div>
          )}
        </div>

        {mode === "movement" ? (
          <section className="calibration-instructions">
            <span className="eyebrow">Head range</span>
            <div className={`movement-icon ${capturing ? "movement-icon--capturing" : ""}`}><CurrentIcon /></div>
            <h1>{CAPTURE_STEPS[step].label}</h1>
            <p>{CAPTURE_STEPS[step].hint}</p>
            <div className="range-dots" aria-label={`${step} steps completed`}>
              {CAPTURE_STEPS.map((item, index) => (
                <span key={item.label} className={index < step ? "complete" : index === step ? "active" : ""} />
              ))}
            </div>
            <button className="button button--primary button--wide" disabled={status !== "tracking" || capturing} onClick={capture}>
              {capturing ? <><span className="loader loader--small" /> Hold that pose…</> : <><Camera /> Capture position</>}
            </button>
            <p className="privacy-note"><Shield /> Video stays on this device and is never saved.</p>
          </section>
        ) : (
          <section className="calibration-instructions trigger-test">
            <span className="eyebrow">Choose your fire trigger</span>
            <h1>Make some noise.<br />Or a funny face.</h1>
            <div className="trigger-options">
              <button className={selectedTrigger === "mouth" ? "trigger-card active" : "trigger-card"} onClick={() => { onTriggerChange("mouth"); setTestedFire(false); initialFireRef.current = fireSignal; }}>
                <span className="trigger-icon mouth-icon">O</span>
                <span><strong>Open mouth</strong><small>Fast & quiet</small></span>
                <span className="trigger-reading">
                  <span className="live-meter"><i style={{ width: `${Math.min(100, blend.mouth * 100)}%` }} /><b style={{ left: `${thresholds.mouthFire * 100}%` }} /></span>
                  <small>{blend.mouth.toFixed(2)} / {thresholds.mouthFire.toFixed(2)}</small>
                </span>
              </button>
              <button className={selectedTrigger === "blink" ? "trigger-card active" : "trigger-card"} onClick={() => { onTriggerChange("blink"); setTestedFire(false); initialFireRef.current = fireSignal; }}>
                <span className="trigger-icon"><Eye /></span>
                <span><strong>Long blink</strong><small>Hands-free</small></span>
                <span className="trigger-reading">
                  <span className="live-meter"><i style={{ width: `${Math.min(100, blend.blink * 100)}%` }} /><b style={{ left: `${thresholds.blinkFire * 100}%` }} /></span>
                  <small>{blend.blink.toFixed(2)} / {thresholds.blinkFire.toFixed(2)}</small>
                </span>
              </button>
              <button disabled={!voiceSupported} className={selectedTrigger === "voice" ? "trigger-card active" : "trigger-card"} onClick={() => { onTriggerChange("voice"); setTestedFire(false); initialFireRef.current = fireSignal; }}>
                <span className="trigger-icon"><Mic2 /></span>
                <span><strong>Say “Pew!”</strong><small>{voiceSupported ? "Most dramatic" : "Not supported here"}</small></span>
              </button>
            </div>
            <div className={testedFire ? "fire-confirm fire-confirm--success" : "fire-confirm"}>
              {testedFire ? <><Check /> Laser fired — nice!</> : <><Sparkles /> Try it now</>}
            </div>
            <button className="button button--primary button--wide" onClick={finish} disabled={!testedFire}>
              <Check /> Finish calibration
            </button>
          </section>
        )}
      </section>
    </main>
  );
}

type SettingsProps = {
  open: boolean;
  muted: boolean;
  reducedMotion: boolean;
  trigger: TriggerMode;
  voiceSupported: boolean;
  profile: CalibrationProfile;
  status: TrackingStatus;
  pose: Pose;
  blend: { mouth: number; blink: number };
  fireSignal: number;
  setVideoElement: (node: HTMLVideoElement | null) => void;
  startCamera: () => Promise<void>;
  onMuted: (value: boolean) => void;
  onReducedMotion: (value: boolean) => void;
  onTrigger: (value: TriggerMode) => void;
  onProfileChange: (profile: CalibrationProfile) => void;
  onSaveDefaults: () => void;
  onResetDefaults: () => void;
  onClose: () => void;
  gamePreview?: ReactNode;
};

const LASER_COLOR_PRESETS = ["#ff375f", "#42e1ff", "#ffd84a", "#72ff9d", "#a66cff"];

export function SettingsPanel(props: SettingsProps) {
  const { open, profile, startCamera } = props;
  const [saved, setSaved] = useState(false);
  const [firePulse, setFirePulse] = useState(0);
  const [simulatorProgress] = useState(0);

  useEffect(() => {
    if (open) void startCamera();
  }, [open, profile, startCamera]);

  useEffect(() => {
    if (props.fireSignal) setFirePulse(props.fireSignal);
  }, [props.fireSignal]);

  const update = (patch: Partial<CalibrationProfile>) => {
    setSaved(false);
    props.onProfileChange({ ...profile, ...patch });
  };
  const viewport = profile.viewport;
  const laser = profile.laser;
  const balloon = profile.balloon;
  const simulatorSpec = useMemo(() => createLevelOne(73, viewport, balloon)[0], [balloon, viewport]);
  const simulatorElapsed = simulatorSpec.spawnAt + simulatorProgress * simulatorSpec.duration;
  const simulatorWorld = balloonPosition(simulatorSpec, simulatorElapsed);
  const previewBalloon = portalScreenPosition(simulatorWorld, props.pose, viewport);
  const simulatorVisible = simulatorProgress >= simulatorSpec.emergeAt;
  const perspectiveHalfHeight = Math.tan(Math.PI * 56 / 360) * Math.max(0.25, 8 - simulatorWorld.z);
  const balloonDiameter = Math.max(1.2, Math.min(52, simulatorSpec.scale * balloonScaleAt(simulatorSpec, simulatorElapsed) / perspectiveHalfHeight * 100));
  const previewBottom = portalScreenPosition({ x: simulatorSpec.startX, y: balloon.fenceBottom, z: -18 }, props.pose, viewport);
  const previewTop = portalScreenPosition({ x: simulatorSpec.startX, y: balloon.fenceTop, z: -18 }, props.pose, viewport);
  const previewDestination = portalScreenPosition({ x: simulatorSpec.targetX, y: simulatorSpec.targetY, z: 6.5 }, props.pose, viewport);
  const imageShiftX = -viewport.framing.x * 50 - props.pose.x * viewport.pan.x * 100 / viewport.zoom;
  const imageShiftY = viewport.framing.y * 50 + props.pose.y * viewport.pan.y * 100 / viewport.zoom;
  const activeScore = props.trigger === "blink" ? props.blend.blink : props.blend.mouth;
  const activeThreshold = props.trigger === "blink" ? profile.thresholds.blinkFire : profile.thresholds.mouthFire;

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="settings-panel settings-panel--lab" role="dialog" aria-modal="true" aria-labelledby="settings-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <span><SlidersHorizontal /> Admin control lab</span>
          <div className="settings-header-actions"><small>Live tuning · no restart needed</small><button className="icon-button" onClick={props.onClose} aria-label="Close settings"><X /></button></div>
        </header>
        <div className="control-lab">
          <section className="control-preview-column">
            <div className="lab-live-game">{props.gamePreview}</div>
            <div
              className="lab-camera lab-game-preview"
              style={{
                "--view-x": `${imageShiftX}%`,
                "--view-y": `${imageShiftY}%`,
                "--view-zoom": `${viewport.zoom * 100}%`,
                "--preview-response": `${24 + profile.smoothing * 180}ms`,
              } as CSSProperties}
            >
              <img className="lab-field-plate" src={publicUrl("assets/backyard-lab/gameplay-field-v1.png")} alt="" />
              <div className="lab-preview-hud"><span>Live flight simulator</span><strong>{simulatorVisible ? "Incoming" : "Behind fence"}</strong></div>
              <div className="lab-scatter-zone" style={{ width: `${Math.max(12, balloon.destinationScatter.x * 68)}%`, height: `${Math.max(12, balloon.destinationScatter.y * 58)}%` }} />
              <div className="lab-fence-mask" style={{ top: `${50 - previewTop.y * 50}%`, height: `${Math.max(2, (previewTop.y - previewBottom.y) * 50)}%` }} />
              <div className="lab-fence-line lab-fence-line--top" style={{ top: `${50 - previewTop.y * 50}%` }}><span>Fence top · emerges here</span></div>
              <div className="lab-fence-line lab-fence-line--bottom" style={{ top: `${50 - previewBottom.y * 50}%` }}><span>Fence bottom · launch ground</span></div>
              <div className="lab-hidden-route" style={{ left: `${50 + previewBottom.x * 50}%`, top: `${50 - previewTop.y * 50}%`, height: `${Math.max(2, (previewTop.y - previewBottom.y) * 50)}%` }} />
              <div className="lab-destination" style={{ left: `${50 + previewDestination.x * 50}%`, top: `${50 - previewDestination.y * 50}%` }}><i />Destination</div>
              <div className={simulatorVisible ? "lab-preview-balloon" : "lab-preview-balloon lab-preview-balloon--hidden"} style={{ left: `${50 + previewBalloon.x * 50}%`, top: `${50 - previewBalloon.y * 50}%`, width: `${balloonDiameter}%` }} />
              <div className="lab-reticle"><span /></div>
              <div className="lab-video-inset"><video ref={props.setVideoElement} muted playsInline /><small>Head input</small></div>
              <div className="lab-tracking-state"><span className={`status-dot ${props.status === "tracking" ? "status-dot--ready" : ""}`} />{props.status === "tracking" ? "Live head tracking" : props.status === "idle" ? "Camera off" : "Starting camera…"}</div>
            </div>
            <div className={`trigger-live-card ${activeScore >= activeThreshold ? "firing" : ""}`} key={firePulse}>
              <header><span><Activity /> Trigger monitor</span><strong>{props.trigger}</strong></header>
              <div className="lab-meter">
                <i style={{ width: `${Math.min(100, activeScore * 100)}%` }} />
                <b style={{ left: `${activeThreshold * 100}%` }} />
              </div>
              <div className="lab-meter-readout"><span>Live <strong>{activeScore.toFixed(3)}</strong></span><span>Fires at <strong>{activeThreshold.toFixed(2)}</strong></span></div>
              <small>{props.trigger === "blink" ? `Fires after ${profile.thresholds.blinkHoldMs} ms closed; reopen to re-arm.` : "Cross the line to fire; close below release to re-arm."}</small>
            </div>
            <div className="lab-legend"><span><i className="legend-live" /> Fixed laser sight</span><span><i className="legend-default" /> Balloon follows world depth</span></div>
          </section>

          <section className="control-settings-column">
            <div className="settings-title-row"><div><span className="eyebrow">Controls, viewport & flight</span><h2 id="settings-title">Tune it live</h2></div><span className="profile-badge">Webcam mapped</span></div>
                <div className="control-section">
                  <div className="control-section-title"><span>Fire trigger</span><small>Thresholds update immediately</small></div>
                  <div className="segmented-control">
                    {(["mouth", "blink", "voice"] as TriggerMode[]).map((mode) => (
                      <button key={mode} disabled={mode === "voice" && !props.voiceSupported} className={props.trigger === mode ? "active" : ""} onClick={() => props.onTrigger(mode)}>{mode}</button>
                    ))}
                  </div>
                  <ControlSlider label="Mouth fire threshold" value={profile.thresholds.mouthFire} min={0.08} max={0.75} step={0.01} display={profile.thresholds.mouthFire.toFixed(2)} onChange={(value) => update({ thresholds: { ...profile.thresholds, mouthFire: value, mouthRelease: Math.min(profile.thresholds.mouthRelease, Math.max(0.03, value - 0.02)) } })} />
                  <ControlSlider label="Mouth re-arm threshold" value={profile.thresholds.mouthRelease} min={0.03} max={Math.max(0.04, profile.thresholds.mouthFire - 0.02)} step={0.01} display={profile.thresholds.mouthRelease.toFixed(2)} onChange={(value) => update({ thresholds: { ...profile.thresholds, mouthRelease: value } })} />
                  <ControlSlider label="Blink fire threshold" value={profile.thresholds.blinkFire} min={0.08} max={0.8} step={0.01} display={profile.thresholds.blinkFire.toFixed(2)} onChange={(value) => update({ thresholds: { ...profile.thresholds, blinkFire: value, blinkRelease: Math.min(profile.thresholds.blinkRelease, Math.max(0.03, value - 0.02)) } })} />
                  <ControlSlider label="Blink re-arm threshold" value={profile.thresholds.blinkRelease} min={0.03} max={Math.max(0.04, profile.thresholds.blinkFire - 0.02)} step={0.01} display={profile.thresholds.blinkRelease.toFixed(2)} onChange={(value) => update({ thresholds: { ...profile.thresholds, blinkRelease: value } })} />
                  <ControlSlider label="Blink hold time" value={profile.thresholds.blinkHoldMs} min={40} max={500} step={10} display={`${profile.thresholds.blinkHoldMs} ms`} onChange={(value) => update({ thresholds: { ...profile.thresholds, blinkHoldMs: value } })} />
                  <div className="threshold-actions">
                    <button onClick={() => {
                      const fire = Math.max(0.08, Math.min(0.7, props.blend.mouth * 0.82));
                      update({ thresholds: { ...profile.thresholds, mouthFire: fire, mouthRelease: Math.max(0.03, fire * 0.42) } });
                    }}>Use live mouth <strong>{props.blend.mouth.toFixed(2)}</strong></button>
                    <button onClick={() => {
                      const fire = Math.max(0.08, Math.min(0.75, props.blend.blink * 0.82));
                      update({ thresholds: { ...profile.thresholds, blinkFire: fire, blinkRelease: Math.max(0.03, fire * 0.38), blinkHoldMs: 60 } });
                    }}>Use live blink <strong>{props.blend.blink.toFixed(2)}</strong></button>
                  </div>
                </div>

                <div className="control-section control-section--laser">
                  <div className="control-section-title"><span>Laser output</span><small>Fire in the sandbox to preview live</small></div>
                  <div className="laser-color-control">
                    <label><span>Custom beam color</span><input aria-label="Custom beam color" type="color" value={laser.color} onChange={(event) => update({ laser: { ...laser, color: event.target.value } })} /></label>
                    <div className="laser-color-presets" aria-label="Laser color presets">
                      {LASER_COLOR_PRESETS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          aria-label={`Use laser color ${color}`}
                          aria-pressed={laser.color.toLowerCase() === color}
                          className={laser.color.toLowerCase() === color ? "active" : ""}
                          style={{ "--laser-swatch": color } as CSSProperties}
                          onClick={() => update({ laser: { ...laser, color } })}
                        />
                      ))}
                    </div>
                  </div>
                  <ControlSlider label="Beam brightness" value={laser.brightness} min={0.65} max={2.5} step={0.05} display={`${Math.round(laser.brightness * 100)}%`} onChange={(value) => update({ laser: { ...laser, brightness: value } })} />
                  <ControlSlider label="Beam width" value={laser.width} min={0.35} max={2.5} step={0.05} display={`${laser.width.toFixed(2)}×`} onChange={(value) => update({ laser: { ...laser, width: value } })} />
                  <ControlSlider label="Active beam time" value={laser.durationMs} min={300} max={1200} step={20} display={`${laser.durationMs} ms`} onChange={(value) => update({ laser: { ...laser, durationMs: value } })} />
                  <small className="laser-upgrade-note">Triple Prism changes the beam to violet and doubles this active hit window.</small>
                </div>

                <div className="control-section">
                  <div className="control-section-title"><span>Camera feel</span><small>Start with a preset, then fine-tune</small></div>
                  <div className="viewport-presets">
                    <button className="recommended" onClick={() => update({ smoothing: DEFAULT_CONTROL_SETTINGS.smoothing, viewport: { ...DEFAULT_CONTROL_SETTINGS.viewport, pan: { ...DEFAULT_CONTROL_SETTINGS.viewport.pan }, framing: viewport.framing } })}><strong>Gentle</strong><span>Recommended</span></button>
                    <button onClick={() => update({ smoothing: 0.56, viewport: { zoom: 1.65, pan: { x: 0.38, y: 0.28 }, depthEffect: 0.32, framing: viewport.framing } })}><strong>Balanced</strong><span>Standard</span></button>
                    <button onClick={() => update({ smoothing: 0.46, viewport: { zoom: 2.05, pan: { x: 0.55, y: 0.4 }, depthEffect: 0.52, framing: viewport.framing } })}><strong>Immersive</strong><span>Close · reactive</span></button>
                  </div>
                  <ControlSlider label="Scene zoom" value={viewport.zoom} min={1.15} max={2.6} step={0.05} display={`${viewport.zoom.toFixed(2)}×`} onChange={(value) => update({ viewport: { ...viewport, zoom: value } })} />
                  <ControlSlider label="Horizontal camera travel" value={viewport.pan.x} min={0.08} max={0.8} step={0.02} display={`${Math.round(viewport.pan.x * 100)}%`} onChange={(value) => update({ viewport: { ...viewport, pan: { ...viewport.pan, x: value } } })} />
                  <ControlSlider label="Vertical camera travel" value={viewport.pan.y} min={0.08} max={0.65} step={0.02} display={`${Math.round(viewport.pan.y * 100)}%`} onChange={(value) => update({ viewport: { ...viewport, pan: { ...viewport.pan, y: value } } })} />
                  <ControlSlider label="Near-object parallax" value={viewport.depthEffect} min={0} max={0.8} step={0.02} display={`${Math.round(viewport.depthEffect * 100)}%`} onChange={(value) => update({ viewport: { ...viewport, depthEffect: value } })} />
                  <ControlSlider label="Viewport smoothing" value={profile.smoothing} min={0} max={0.88} step={0.02} display={`${Math.round(profile.smoothing * 100)}%`} onChange={(value) => update({ smoothing: value })} />
                </div>

                <div className="control-section control-section--physics">
                  <div className="control-section-title"><span>Balloon flight</span><small>Same model used in gameplay</small></div>
                  <div className="control-subgroup"><strong>Backyard scale</strong><span>Physical throw proportions</span></div>
                  <ControlSlider label="Yard depth" value={balloon.yardDepthFt} min={60} max={140} step={2} display={`${Math.round(balloon.yardDepthFt)} ft`} onChange={(value) => update({ balloon: { ...balloon, yardDepthFt: value } })} />
                  <ControlSlider label="Thrower behind fence" value={balloon.throwerDistanceFt} min={6} max={32} step={1} display={`${Math.round(balloon.throwerDistanceFt)} ft`} onChange={(value) => update({ balloon: { ...balloon, throwerDistanceFt: value } })} />
                  <ControlSlider label="Fence height" value={balloon.fenceHeightFt} min={5} max={10} step={0.5} display={`${balloon.fenceHeightFt.toFixed(1)} ft`} onChange={(value) => update({ balloon: { ...balloon, fenceHeightFt: value } })} />
                  <div className="control-subgroup"><strong>Photo registration</strong><span>Launch below, reveal just above</span></div>
                  <ControlSlider label="Fence top / reveal line" value={balloon.fenceTop} min={-0.38} max={0.05} step={0.01} display={balloon.fenceTop.toFixed(2)} onChange={(value) => update({ balloon: { ...balloon, fenceTop: Math.max(value, balloon.fenceBottom + 0.08) } })} />
                  <ControlSlider label="Fence bottom / ground" value={balloon.fenceBottom} min={-0.75} max={-0.16} step={0.01} display={balloon.fenceBottom.toFixed(2)} onChange={(value) => update({ balloon: { ...balloon, fenceBottom: Math.min(value, balloon.fenceTop - 0.08) } })} />
                  <div className="control-subgroup"><strong>Scale & throw</strong><span>Perspective growth and slingshot feel</span></div>
                  <ControlSlider label="Emergence size" value={balloon.initialScale} min={0.12} max={0.9} step={0.02} display={`${Math.round(balloon.initialScale * 100)}%`} onChange={(value) => update({ balloon: { ...balloon, initialScale: Math.min(value, balloon.finalScale) } })} />
                  <ControlSlider label="Impact size" value={balloon.finalScale} min={0.45} max={1.5} step={0.05} display={`${Math.round(balloon.finalScale * 100)}%`} onChange={(value) => update({ balloon: { ...balloon, finalScale: Math.max(value, balloon.initialScale) } })} />
                  <ControlSlider label="Arc height" value={balloon.arc} min={0.35} max={2.2} step={0.05} display={`${balloon.arc.toFixed(2)}×`} onChange={(value) => update({ balloon: { ...balloon, arc: value } })} />
                  <ControlSlider label="Flight time" value={balloon.flightTime} min={0.65} max={1.65} step={0.05} display={`${balloon.flightTime.toFixed(2)}×`} onChange={(value) => update({ balloon: { ...balloon, flightTime: value } })} />
                  <ControlSlider label="Launch cadence" value={balloon.cadence} min={0.55} max={1.45} step={0.05} display={`${balloon.cadence.toFixed(2)}×`} onChange={(value) => update({ balloon: { ...balloon, cadence: value } })} />
                  <div className="control-subgroup"><strong>Scatter</strong><span>Origin and reachable destinations</span></div>
                  <ControlSlider label="Origin scatter" value={balloon.originScatter} min={0} max={0.25} step={0.01} display={balloon.originScatter.toFixed(2)} onChange={(value) => update({ balloon: { ...balloon, originScatter: value } })} />
                  <ControlSlider label="Destination width" value={balloon.destinationScatter.x} min={0.25} max={1} step={0.05} display={`${Math.round(balloon.destinationScatter.x * 100)}%`} onChange={(value) => update({ balloon: { ...balloon, destinationScatter: { ...balloon.destinationScatter, x: value } } })} />
                  <ControlSlider label="Destination height" value={balloon.destinationScatter.y} min={0.25} max={1} step={0.05} display={`${Math.round(balloon.destinationScatter.y * 100)}%`} onChange={(value) => update({ balloon: { ...balloon, destinationScatter: { ...balloon.destinationScatter, y: value } } })} />
                  <button className="physics-reset" onClick={() => update({ balloon: { ...DEFAULT_CONTROL_SETTINGS.balloon, destinationScatter: { ...DEFAULT_CONTROL_SETTINGS.balloon.destinationScatter } } })}><RotateCcw /> Reset balloon flight</button>
                </div>

                <div className="control-section">
                  <div className="control-section-title"><span>Webcam angle mapping</span><small>Frame edges map to maximum view angle</small></div>
                  <ControlSlider label="Horizontal sensitivity" value={profile.sensitivity.x} min={0.3} max={2.5} step={0.05} display={`${profile.sensitivity.x.toFixed(2)}×`} onChange={(value) => update({ sensitivity: { ...profile.sensitivity, x: value } })} />
                  <ControlSlider label="Vertical sensitivity" value={profile.sensitivity.y} min={0.3} max={2.5} step={0.05} display={`${profile.sensitivity.y.toFixed(2)}×`} onChange={(value) => update({ sensitivity: { ...profile.sensitivity, y: value } })} />
                  <div className="flip-controls">
                    <button className={profile.flip.x ? "active" : ""} onClick={() => update({ flip: { ...profile.flip, x: !profile.flip.x } })}><ArrowLeftRight /> Flip left / right <span>{profile.flip.x ? "On" : "Off"}</span></button>
                    <button className={profile.flip.y ? "active" : ""} onClick={() => update({ flip: { ...profile.flip, y: !profile.flip.y } })}><ArrowUpDown /> Flip up / down <span>{profile.flip.y ? "On" : "Off"}</span></button>
                  </div>
                </div>

                <div className="control-section">
                  <div className="control-section-title"><span>Neutral framing</span><small>Move the yard, never the laser sight</small></div>
                  <ControlSlider label="Frame left / right" value={viewport.framing.x} min={-0.35} max={0.35} step={0.01} display={viewport.framing.x.toFixed(2)} onChange={(value) => update({ viewport: { ...viewport, framing: { ...viewport.framing, x: value } } })} />
                  <ControlSlider label="Frame down / up" value={viewport.framing.y} min={-0.3} max={0.3} step={0.01} display={viewport.framing.y.toFixed(2)} onChange={(value) => update({ viewport: { ...viewport, framing: { ...viewport.framing, y: value } } })} />
                </div>
            <div className="control-utility-row">
              <label className="compact-toggle"><input type="checkbox" checked={!props.muted} onChange={(event) => props.onMuted(!event.target.checked)} />{props.muted ? <VolumeX /> : <Volume2 />} Audio</label>
              <label className="compact-toggle"><input type="checkbox" checked={!props.reducedMotion} onChange={(event) => props.onReducedMotion(!event.target.checked)} /><Sparkles /> Full motion</label>
            </div>
            <div className="control-save-row">
              <button className="button button--ghost" onClick={() => { props.onResetDefaults(); setSaved(false); }}><RotateCcw /> Recommended defaults</button>
              <button className="button button--primary" onClick={() => { props.onSaveDefaults(); setSaved(true); }}><Save /> {saved ? "Defaults saved" : "Save as defaults"}</button>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}

function ControlSlider({ label, value, min, max, step, display, onChange }: { label: string; value: number; min: number; max: number; step: number; display: string; onChange: (value: number) => void }) {
  return (
    <label className="control-slider">
      <span>{label}<strong>{display}</strong></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

export function ResultScreen({ result, onReplay, onMenu }: { result: GameResult; onReplay: () => void; onMenu: () => void }) {
  const accuracy = result.shots ? Math.round((result.accurateShots / result.shots) * 100) : 0;
  const won = result.outcome === "won";
  return (
    <main className={`result-screen ${won ? "result-screen--win" : "result-screen--lose"} screen-enter`}>
      <section className="result-art" aria-hidden="true">
        <img src={publicUrl(won ? "assets/backyard-lab/result-victory-v2.png" : "assets/backyard-lab/result-shutdown-v3.png")} alt="" />
      </section>
      <section className="result-copy">
        <span className="eyebrow">{won ? "Backyard secured" : "System status"}</span>
        <h1>{won ? <>Campaign<br /><em>complete!</em></> : <>Laser defense<br /><em>offline</em></>}</h1>
        <p>{won ? "All ten backyard assaults have been intercepted." : `Shield lost on level ${result.levelReached} of 10.`}</p>
        <div className="result-stats">
          <div><strong>{result.score.toLocaleString()}</strong><span>Score</span></div>
          <div><strong>{accuracy}%</strong><span>Accuracy</span></div>
          <div><strong>{result.popped}<small>/{result.balloonsFaced}</small></strong><span>Intercepted</span></div>
          <div><strong>x{result.maxCombo}</strong><span>Best combo</span></div>
        </div>
        <div className="result-actions">
          <button className="button button--primary button--huge" onClick={onReplay}><RotateCcw /> Restart campaign</button>
          <button className="button button--ghost" onClick={onMenu}>Main menu</button>
        </div>
      </section>
    </main>
  );
}
